import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Icon, Label, List, Button, Divider } from "semantic-ui-react";
import { format } from "date-fns";
import { openModal } from "../../../app/common/modals/modalSlice";
import { functionGetStrategyPropertiesForTransaction } from "../../../app/firestore/functionsService";
import { buildPropertyKey } from "../../../app/common/util/strategyPropertyKey";
import { isStrategyVisible } from "./strategyComponents/strategyVisibility";

const STRATEGY_TYPE_LABELS = { pricing: "Pricing Strategy", listingDescription: "Listing Description Strategy", propertyIntelligence: "Property Intelligence" };

// Which modal opens a read-only view of a non-current property's saved
// Strategy - keyed by strategyType so this stays a one-line addition for any
// future strategy type, rather than a growing if/else chain.
const STRATEGY_TYPE_VIEWER_MODAL = { pricing: "StrategyResultViewerModal", listingDescription: "StrategyListingDescriptionResultViewerModal" };

function toDisplayDate(value) {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : format(date, "MMM d, yyyy");
}

// Buyer multi-property Strategy history (spec sections 29-31/63) - once a
// transaction has Strategies for more than one address, every property gets
// its own grouped section rather than one silently overwriting another.
// Single-property transactions (the common case) render nothing here; the
// existing flat card layout above covers them.
export default function TransactionStrategyPropertyGroups({ transaction, refreshSignal }) {
  const dispatch = useDispatch();
  const [properties, setProperties] = useState([]);

  const currentPropertyKey = buildPropertyKey(transaction?.address);
  const transactionId = transaction?.id;

  // refreshSignal: bumped by TransactionStrategyDashboard whenever a
  // generation succeeds (Pricing or Listing Description) - without it, this
  // only ever fetches once on mount, so switching a Buyer transaction to a
  // new address mid-session and generating a Strategy for it never shows up
  // here until the page is actually reloaded/revisited.
  useEffect(() => {
    if (!transactionId) return;
    let cancelled = false;
    functionGetStrategyPropertiesForTransaction({ transactionId })
      .then((data) => {
        if (!cancelled) setProperties(data);
      })
      .catch(() => {
        if (!cancelled) setProperties([]);
      });
    return () => {
      cancelled = true;
    };
  }, [transactionId, refreshSignal]);

  if (properties.length <= 1) return null;

  const sorted = [...properties].sort((a, b) => {
    if (a.propertyKey === currentPropertyKey) return -1;
    if (b.propertyKey === currentPropertyKey) return 1;
    return 0;
  });

  function handleViewStrategy(property, strategyType) {
    const strategyEntry = property.strategies?.[strategyType];
    if (!strategyEntry) return;
    if (strategyType === "propertyIntelligence") {
      dispatch(openModal({ modalType: "PropertyIntelligenceModal", modalProps: { transaction, propertyKey: property.propertyKey } }));
      return;
    }
    dispatch(
      openModal({
        modalType: STRATEGY_TYPE_VIEWER_MODAL[strategyType] || "StrategyResultViewerModal",
        modalProps: {
          transactionId: transaction.id,
          transaction,
          propertyKey: property.propertyKey,
          propertyAddress: property.propertyAddress,
          // Listing Description tracks a selected version separately from
          // the latest generation (spec section 52) - prefer that when set.
          generationId: strategyEntry.selectedGenerationId || strategyEntry.generationId,
          // Fallback only - the viewer prefers the representation the
          // Strategy was actually generated under.
          agentRepresents: transaction.agentRepresents,
        },
      })
    );
  }

  // The current property already has its own live, actionable card(s) above
  // (with working Generate/Regenerate buttons) - listing it again here would
  // just duplicate that with a dead-end "Use the card above" row, so this
  // only ever shows OTHER (previous) properties, each as its own row: the
  // address at the top, then that property's Strategies underneath it.
  const previousProperties = sorted.filter((property) => property.propertyKey !== currentPropertyKey);
  if (previousProperties.length === 0) return null;

  return (
    <div style={{ marginTop: "24px" }}>
      <h3>Previous Properties</h3>
      {previousProperties.map((property, idx) => {
        // Hidden Strategy types (strategyVisibility.js) keep their saved
        // records but aren't listed; a reset Property Intelligence report is gone.
        const strategyTypes = Object.keys(property.strategies || {}).filter(
          (type) => isStrategyVisible(type) && !(type === "propertyIntelligence" && property.strategies[type]?.status === "reset")
        );
        return (
          <div key={property.propertyKey} style={{ marginTop: idx === 0 ? 0 : "20px" }}>
            <h4 style={{ marginBottom: "4px" }}>
              <Icon name="home" />
              {property.propertyAddress?.street}
              {property.propertyAddress?.unit ? ` ${property.propertyAddress.unit}` : ""}
              {", "}
              {property.propertyAddress?.city}, {property.propertyAddress?.state}
              <Label size="small" color="grey" style={{ marginLeft: "10px", verticalAlign: "middle" }}>
                Previous Property
              </Label>
            </h4>
            {strategyTypes.length === 0 ? (
              <p className="text-grey">No Strategies generated yet.</p>
            ) : (
              <List divided relaxed>
                {strategyTypes.map((strategyType) => (
                  <List.Item key={strategyType}>
                    <List.Content floated="right">
                      <Button size="mini" basic onClick={() => handleViewStrategy(property, strategyType)}>
                        View
                      </Button>
                    </List.Content>
                    <List.Content>
                      {STRATEGY_TYPE_LABELS[strategyType] || strategyType}
                      {(property.strategies[strategyType]?.generatedAt || property.strategies[strategyType]?.completedAt) && (
                        <span className="text-grey" style={{ marginLeft: "8px", fontSize: "0.85em" }}>
                          Generated {toDisplayDate(property.strategies[strategyType].generatedAt || property.strategies[strategyType].completedAt)}
                        </span>
                      )}
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            )}
            {idx < previousProperties.length - 1 && <Divider />}
          </div>
        );
      })}
    </div>
  );
}
